import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Phone, Mail, ExternalLink, AlertTriangle } from 'lucide-react';

const Klachtenregeling = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Klachtenregeling | Klacht indienen stap voor stap | Compleet Verzekerd</title>
        <meta name="description" content="Klachtenprocedure Compleet Verzekerd. Transparante behandeling binnen 4 weken. Van direct contact tot KiFiD en AFM. Gratis klachtbehandeling." />
        <meta name="keywords" content="klacht indienen, klachtenprocedure, klachtenbehandeling, kifid klacht, afm klacht, klachtenregeling, geschillencommissie, klantenservice" />
        <link rel="canonical" href="https://compleetverzekerd.nl/klachtenregeling/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Klachtenregeling",
            "description": "Procedure voor het indienen en behandelen van klachten bij Compleet Verzekerd",
            "publisher": {
              "@type": "Organization",
              "name": "Compleet Verzekerd",
              "telephone": "0782200021"
            }
          })}
        </script>
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      <Header />
      
      <main id="main-content" className="pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">Klachtenregeling</h1>
            <p className="text-xl text-muted-foreground">
              Procedure voor het indienen en behandelen van klachten
            </p>
          </div>

          <div className="space-y-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 dark:bg-blue-950/20 dark:border-blue-900/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    Heeft u een klacht?
                  </h3>
                  <p className="text-blue-700 dark:text-blue-300">
                    Wij nemen klachten serieus en streven ernaar deze zo snel mogelijk op te lossen. 
                    Hieronder vindt u de stappen die u kunt ondernemen.
                  </p>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Stap 1: Direct contact</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Heeft u een klacht over onze dienstverlening? Neem dan eerst direct contact 
                  met ons op. Vaak kunnen we uw klacht direct oplossen door een persoonlijk gesprek.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                    <Phone className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Telefoon</p>
                      <a href="tel:0782200021" className="text-sm text-muted-foreground hover:text-primary transition-colors">078 - 220 00 21</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">E-mail</p>
                      <p className="text-sm text-muted-foreground">info@compleetverzekerd.nl</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Stap 2: Schriftelijke klacht</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Komt u er samen met ons niet uit, dan kunt u uw klacht schriftelijk indienen. 
                  Vermeld daarbij de volgende gegevens:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
                  <li>Uw naam en contactgegevens</li>
                  <li>Datum van het voorval</li>
                  <li>Omschrijving van de klacht</li>
                  <li>Wat er volgens u mis is gegaan</li>
                  <li>Welke oplossing u voor ogen heeft</li>
                  <li>Relevante documenten (kopiëen)</li>
                </ul>
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Klacht indienen bij:</h4>
                  <p className="text-sm text-muted-foreground">
                    Compleet Verzekerd<br />
                    t.a.v. Klachtbehandeling<br />
                    Van Eesterenplein 95<br />
                    3315 KT Dordrecht<br />
                    E-mail: info@compleetverzekerd.nl
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Onze behandeling van uw klacht</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="bg-primary/10 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold text-primary">1</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Ontvangstbevestiging</h4>
                      <p className="text-sm text-muted-foreground">
                        Binnen 3 werkdagen ontvangt u van ons een ontvangstbevestiging 
                        met daarin de naam van de behandelaar.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="bg-primary/10 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold text-primary">2</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Onderzoek</h4>
                      <p className="text-sm text-muted-foreground">
                        Wij onderzoeken uw klacht zorgvuldig en nemen indien nodig 
                        contact met u op voor aanvullende informatie.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="bg-primary/10 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold text-primary">3</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Reactie</h4>
                      <p className="text-sm text-muted-foreground">
                        Binnen 4 weken ontvangt u van ons een gemotiveerde reactie op uw klacht. 
                        In uitzonderlijke gevallen kan dit langer duren.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Niet tevreden met onze reactie?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Bent u niet tevreden met de afhandeling van uw klacht? Dan kunt u zich wenden tot:
                </p>

                <div className="space-y-6">
                  <div className="border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Klachteninstituut Financiële Dienstverlening (KiFiD)
                    </h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Postbus 93257</p>
                      <p>2509 AG Den Haag</p>
                      <p>Telefoon: 0900 - 355 22 48</p>
                      <p>Website: www.kifid.nl</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => window.open('https://www.kifid.nl', '_blank')}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Naar KiFiD website
                    </Button>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Autoriteit Financiële Markten (AFM)
                    </h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Postbus 11723</p>
                      <p>1001 GS Amsterdam</p>
                      <p>Telefoon: 020 - 797 20 00</p>
                      <p>Website: www.afm.nl</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => window.open('https://www.afm.nl', '_blank')}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Naar AFM website
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Kosten</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Het indienen van een klacht bij ons is altijd kosteloos. 
                  Voor het indienen van een klacht bij het KiFiD kunnen kosten in rekening worden gebracht. 
                  Informatie hierover vindt u op de website van het KiFiD.
                </p>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button 
                size="lg" 
                onClick={() => {
                  window.location.href = '/contact#bericht';
                }}
                className="bg-primary hover:bg-primary/90"
              >
                <Mail className="mr-2 h-4 w-4" />
                Direct contact opnemen
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Klachtenregeling;